import { Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider, createQueryClient } from "@project/ui";
import { Toaster } from "sonner";
import { I18nProvider } from "@apps/dashboard/contexts/I18nContext";
import { AppShell } from "@apps/dashboard/components/layout/AppShell";
import ExpenseDetailPage from "@apps/dashboard/pages/ExpenseDetailPage";
import ExpensePaidFeedbackPage from "@apps/dashboard/pages/ExpensePaidFeedbackPage";
import ExpenseSeriesDetailPage from "@apps/dashboard/pages/ExpenseSeriesDetailPage";
import ExpenseNewPage from "@apps/dashboard/pages/ExpenseNewPage";
import ExpensesPage from "@apps/dashboard/pages/ExpensesPage";
import HomePage from "@apps/dashboard/pages/HomePage";
import HouseMembersPage from "@apps/dashboard/pages/HouseMembersPage";
import HousesPage from "@apps/dashboard/pages/HousesPage";
import BudgetPage from "@apps/dashboard/pages/BudgetPage";
import HouseAnalyticsPage from "@apps/dashboard/pages/HouseAnalyticsPage";
import SettingsPage from "@apps/dashboard/pages/SettingsPage";
import SettingsPasswordPage from "@apps/dashboard/pages/SettingsPasswordPage";
import SettingsPasskeysPage from "@apps/dashboard/pages/SettingsPasskeysPage";
import SupportPage from "@apps/dashboard/pages/SupportPage";
import NotificationSettingsPage from "@apps/dashboard/pages/NotificationSettingsPage";
import NotificationsInboxPage from "@apps/dashboard/pages/NotificationsInboxPage";
import PendingHouseInvitesPage from "@apps/dashboard/pages/PendingHouseInvitesPage";
import SettingsDevicesPage from "@apps/dashboard/pages/SettingsDevicesPage";
import SettingsPersonalDataPage from "@apps/dashboard/pages/SettingsPersonalDataPage";
import ScanPage from "@apps/dashboard/pages/ScanPage";
import SearchPage from "@apps/dashboard/pages/SearchPage";
import VaultDetailPage from "@apps/dashboard/pages/VaultDetailPage";
import VaultListPage from "@apps/dashboard/pages/VaultListPage";
import VaultNewPage from "@apps/dashboard/pages/VaultNewPage";
import FinancingsListPage from "@apps/dashboard/pages/FinancingsListPage";
import FinancingNewPage from "@apps/dashboard/pages/FinancingNewPage";
import FinancingDetailPage from "@apps/dashboard/pages/FinancingDetailPage";
import FinancingEditPage from "@apps/dashboard/pages/FinancingEditPage";
import { ProtectedRoute } from "@apps/dashboard/components/auth/ProtectedRoute";
import { GuestOnlyRoute } from "@apps/dashboard/components/auth/GuestOnlyRoute";
import { ProtectedHouseOutlet } from "@apps/dashboard/components/auth/ProtectedHouseOutlet";
import { OnboardingGate } from "@apps/dashboard/components/auth/OnboardingGate";
import LoginPage from "@apps/dashboard/pages/LoginPage";
import RegisterPage from "@apps/dashboard/pages/RegisterPage";
import ForgotPasswordPage from "@apps/dashboard/pages/ForgotPasswordPage";
import ResetPasswordPage from "@apps/dashboard/pages/ResetPasswordPage";
import OnboardingPage from "@apps/dashboard/pages/OnboardingPage";

const queryClient = createQueryClient();

function App(): JSX.Element {
  return (
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <Routes>
          <Route element={<GuestOnlyRoute />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route element={<ProtectedHouseOutlet />}>
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route element={<AppShell />}>
                <Route path="invitations/pending" element={<PendingHouseInvitesPage />} />
                <Route element={<OnboardingGate />}>
                  <Route index element={<HomePage />} />
                  <Route path="home" element={<Navigate to="/" replace />} />
                  <Route path="search" element={<SearchPage />} />
                  <Route path="notifications" element={<NotificationsInboxPage />} />

                  <Route path="settings/personal" element={<SettingsPersonalDataPage />} />
                  <Route path="settings/password" element={<SettingsPasswordPage />} />
                  <Route path="settings/passkeys" element={<SettingsPasskeysPage />} />
                  <Route path="settings/notifications" element={<NotificationSettingsPage />} />
                  <Route path="settings/devices" element={<SettingsDevicesPage />} />
                  <Route path="settings/profile" element={<Navigate to="/settings/personal" replace />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="support" element={<SupportPage />} />

                  <Route path="houses" element={<HousesPage />} />
                  <Route path="houses/:houseId/members" element={<HouseMembersPage />} />

                  <Route path="houses/:houseId/expenses/new" element={<ExpenseNewPage />} />
                  <Route path="houses/:houseId/expense-series/:seriesId" element={<ExpenseSeriesDetailPage />} />
                  <Route path="houses/:houseId/expenses/:expenseId/paid-feedback" element={<ExpensePaidFeedbackPage />} />
                  <Route path="houses/:houseId/expenses/:expenseId" element={<ExpenseDetailPage />} />
                  <Route path="houses/:houseId/expenses" element={<ExpensesPage />} />

                  <Route path="houses/:houseId/financings/new" element={<FinancingNewPage />} />
                  <Route path="houses/:houseId/financings/:financingId/edit" element={<FinancingEditPage />} />
                  <Route path="houses/:houseId/financings/:financingId" element={<FinancingDetailPage />} />
                  <Route path="houses/:houseId/financings" element={<FinancingsListPage />} />

                  <Route path="houses/:houseId/budget" element={<BudgetPage />} />
                  <Route path="houses/:houseId/analytics" element={<HouseAnalyticsPage />} />

                  <Route path="houses/:houseId/vault/new" element={<VaultNewPage />} />
                  <Route path="houses/:houseId/vault/:itemId" element={<VaultDetailPage />} />
                  <Route path="houses/:houseId/vault" element={<VaultListPage />} />

                  <Route path="houses/:houseId/scan" element={<ScanPage />} />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster closeButton position="top-right" />
      </QueryClientProvider>
    </I18nProvider>
  );
}

export default App;
